import React from 'react';
import { Card } from '../../../state/ArenaTypes';
import { Check, Loader2, ArrowLeft, ArrowRight, ArrowUp, Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AuctionTurnTimerBadge } from '../../../components/Auction/AuctionTurnTimerBadge';
import { CardView } from '../../../components/Card/CardView';
import { arenaAudio } from '../../../services/ArenaAudioService';

export interface HeartsPassingHudProps {
  selectedPassCount: number;
  passingDirection: 'LEFT' | 'RIGHT' | 'ACROSS' | 'HOLD' | 'NONE';
  passedCards?: Card[];
  onUnpassCard?: (card: Card) => void;
  turnTimerSeconds?: number | null;
  turnTimerMax?: number;
  isSubmittingPass: boolean;
  onConfirmPass?: () => void;
  hasPassedCards: boolean;
}

export const HeartsPassingHud: React.FC<HeartsPassingHudProps> = ({
  selectedPassCount,
  passingDirection = 'LEFT',
  passedCards = [],
  onUnpassCard,
  turnTimerSeconds,
  turnTimerMax = 30,
  isSubmittingPass,
  onConfirmPass,
  hasPassedCards,
}) => {
  const lastCardsRef = React.useRef<Card[]>(passedCards);
  if (isSubmittingPass && passedCards.length > 0) {
    lastCardsRef.current = passedCards;
  }
  const effectivePassedCards = isSubmittingPass && passedCards.length === 0
    ? lastCardsRef.current
    : passedCards;
  const effectiveCount = isSubmittingPass && selectedPassCount === 0
    ? effectivePassedCards.length
    : selectedPassCount;
  const isReady = effectiveCount === 3;

  const maxTimer = turnTimerMax && turnTimerMax > 0 ? turnTimerMax : 30;
  const redThreshold = Math.max(5, Math.round(maxTimer * 0.25));
  const isCritical = turnTimerSeconds !== undefined && turnTimerSeconds !== null && turnTimerSeconds <= redThreshold;

  const handleConfirmClick = React.useCallback(() => {
    if (!isReady || isSubmittingPass || hasPassedCards) return;
    try {
      arenaAudio.playCardFan();
    } catch {}
    onConfirmPass?.();
  }, [isReady, isSubmittingPass, hasPassedCards, onConfirmPass]);

  const directionLabel = React.useMemo(() => {
    switch (passingDirection) {
      case 'LEFT': return 'Pass 3 Cards Left';
      case 'RIGHT': return 'Pass 3 Cards Right';
      case 'ACROSS': return 'Pass 3 Cards Across';
      case 'HOLD':
      case 'NONE':
        return 'No Passing (Hold Hand)';
      default:
        return 'Pass 3 Cards';
    }
  }, [passingDirection]);

  const DirectionIcon = React.useMemo(() => {
    switch (passingDirection) {
      case 'LEFT': return ArrowLeft;
      case 'RIGHT': return ArrowRight;
      case 'ACROSS': return ArrowUp;
      default: return Heart;
    }
  }, [passingDirection]);

  return (
    <motion.div
      key="hearts-passing-hud"
      initial={{ opacity: 0, scale: 0.95, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{
        opacity: 0,
        scale: 0.95,
        y: 12,
        transition: { duration: 0.2, ease: [0.32, 0, 0.67, 0] }
      }}
      transition={{ type: 'spring', damping: 24, stiffness: 300, mass: 0.8 }}
      className="w-[calc(100%-1rem)] max-w-[460px] sm:max-w-[520px] md:max-w-[560px] pointer-events-auto select-none rounded-[28px] bg-[#0a1120] transition-[border-color,box-shadow] duration-300 transform-gpu [backface-visibility:hidden] [webkit-backface-visibility:hidden] relative"
      style={{
        border: isCritical ? '1.5px solid #f43f5e' : '1.5px solid #e11d48',
        boxShadow: isCritical
          ? '0 0 10px 1px rgba(244, 63, 94, 0.5), 0 16px 45px rgba(0, 0, 0, 0.85)'
          : '0 0 10px 1px rgba(225, 29, 72, 0.35), 0 16px 45px rgba(0, 0, 0, 0.85)',
        WebkitBackfaceVisibility: 'hidden',
        willChange: 'transform, opacity',
      }}
    >
      {/* CIRCULAR TIMER BADGE */}
      {turnTimerSeconds !== undefined && turnTimerSeconds !== null && !hasPassedCards && (
        <AuctionTurnTimerBadge
          turnTimerSeconds={turnTimerSeconds}
          turnTimerMax={turnTimerMax}
          className="absolute -top-7 left-1/2 -translate-x-1/2 z-30"
        />
      )}

      {/* INNER GRADIENT CONTAINER */}
      <div className="w-full h-full rounded-[27px] px-3 sm:px-5 pt-6 sm:pt-7 pb-2.5 sm:pb-3 relative z-10 flex flex-col space-y-2 sm:space-y-2.5 bg-gradient-to-b from-[#1a0a14] via-[#120810] to-[#080306]">
        {/* Subtle Watermarks */}
        <div className="absolute inset-0 rounded-[27px] overflow-hidden pointer-events-none select-none">
          <Heart className="absolute top-1 left-2 w-20 h-20 sm:w-24 sm:h-24 rotate-[15deg] text-rose-500/[0.06]" />
          <Heart className="absolute top-1 right-2 w-20 h-20 sm:w-24 sm:h-24 -rotate-[15deg] text-rose-500/[0.06]" />
        </div>

        {/* SECTION 1: HEADER & TITLE */}
        <div className="flex flex-col items-center justify-center text-center relative z-10 w-full pt-0.5">
          <div className="flex items-center justify-center gap-2 sm:gap-3 w-full px-1">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-rose-500/40 to-rose-500/70" />
            <h2 className="text-xl sm:text-2xl font-black tracking-tight leading-tight shrink-0 flex items-center gap-1.5">
              <DirectionIcon size={20} className="text-rose-400" />
              <span className="text-rose-400">{directionLabel}</span>
            </h2>
            <div className="flex-1 h-px bg-gradient-to-l from-transparent via-rose-500/40 to-rose-500/70" />
          </div>

          <div className="flex items-center justify-center mt-1">
            {hasPassedCards ? (
              <span className="text-[11px] sm:text-xs font-black px-3 py-0.5 rounded-full border bg-emerald-500/20 text-emerald-300 border-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.3)]">
                ✓ Cards Passed — Waiting for other players
              </span>
            ) : (
              <span
                className={`text-[10.5px] sm:text-[11.5px] font-black px-2.5 py-0.5 rounded-full border transition-all ${
                  isReady
                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.3)]'
                    : 'bg-[#180914] text-rose-300 border-rose-500/30'
                }`}
              >
                {effectiveCount} / 3 Cards Selected
              </span>
            )}
          </div>
        </div>

        {/* SEPARATOR LINE */}
        <div className="w-full h-px bg-gradient-to-r from-transparent via-rose-500/30 to-transparent my-0.5 relative z-10" />

        {/* SECTION 2: 3 PASS SLOTS TRAY - Sized so cards fit inside slots perfectly without overflow */}
        <div className="w-full flex items-center justify-center gap-3 sm:gap-4 py-1.5 relative z-10 min-h-[96px] sm:min-h-[110px]">
          {Array.from({ length: 3 }).map((_, idx) => {
            const card = effectivePassedCards[idx];
            return (
              <div
                key={idx}
                className={`relative w-[76px] h-[106px] sm:w-[86px] sm:h-[120px] rounded-xl flex items-center justify-center transition-all overflow-hidden ${
                  card
                    ? 'shadow-[0_6px_16px_rgba(0,0,0,0.7)] cursor-pointer ring-1 ring-rose-400/50 hover:ring-rose-300 hover:scale-[1.03] active:scale-[0.97]'
                    : 'border-2 border-dashed border-rose-500/40 bg-black/50'
                }`}
                onClick={() => {
                  if (card && onUnpassCard && !isSubmittingPass && !hasPassedCards) {
                    onUnpassCard(card);
                  }
                }}
              >
                <AnimatePresence mode="wait">
                  {card ? (
                    <motion.div
                      key={card.id}
                      initial={{ scale: 0.65, opacity: 0, y: 24, rotate: -3 }}
                      animate={{ scale: 1, opacity: 1, y: 0, rotate: 0 }}
                      exit={{ scale: 0.65, opacity: 0, y: 24, transition: { duration: 0.18 } }}
                      transition={{ type: 'spring', damping: 22, stiffness: 320, mass: 0.5 }}
                      className="w-full h-full flex items-center justify-center pointer-events-none select-none"
                    >
                      <CardView
                        card={card}
                        size="sm"
                        isPlayable={false}
                        hasShadow={false}
                        isHearts={true}
                        className="scale-[0.90] sm:scale-[0.98] origin-center"
                      />
                    </motion.div>
                  ) : (
                    <motion.span
                      key={`placeholder_${idx}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-xs font-bold text-rose-400/40 font-mono"
                    >
                      #{idx + 1}
                    </motion.span>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* SECTION 3: ACTION BUTTON */}
        {!hasPassedCards && (
          <div className="w-full pt-1 relative z-10">
            <button
              type="button"
              disabled={!isReady || isSubmittingPass}
              onClick={handleConfirmClick}
              className={`w-full py-2.5 sm:py-3 rounded-2xl font-black text-xs sm:text-sm uppercase tracking-wider flex items-center justify-center gap-2 transition-all ${
                isReady && !isSubmittingPass
                  ? 'bg-gradient-to-r from-rose-600 via-rose-500 to-red-600 hover:from-rose-500 hover:to-red-500 text-white shadow-[0_0_20px_rgba(225,29,72,0.5)] active:scale-[0.98]'
                  : 'bg-white/5 text-white/30 border border-white/10 cursor-not-allowed'
              }`}
            >
              {isSubmittingPass ? (
                <>
                  <Loader2 size={16} className="animate-spin text-rose-300" />
                  <span>Passing Cards...</span>
                </>
              ) : (
                <>
                  <Check size={16} className={isReady ? 'text-white' : 'text-white/30'} />
                  <span>{directionLabel}</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </motion.div>
  );
};
